import type {
    Map,
    LineLayerSpecification,
    FillLayerSpecification,
    ModelLayerSpecification,
    LayerSpecification,
    FillExtrusionLayerSpecification,
    CircleLayerSpecification,
    SymbolLayerSpecification,
} from "@iventis/mapbox-gl";
import type { Feature, Geometry } from "geojson";
import { createMapboxLevelFilter } from "../mapbox/filters/engine-mapbox-level-filter-helpers";
import { removeLayerFilterByType, updateLayerFilters } from "../mapbox/filters/engine-mapbox-filter-helpers";
import { createDateFilterExpressionForLocalLayer } from "../mapbox/filters/engine-mapbox-date-filter-helpers";
import { FilterType } from "../mapbox/filters/engine-mapbox-filter-types";
import { IMapboxLayer } from "./mapbox-layer";

type SupportedMapboxLayers =
    | FillLayerSpecification
    | FillExtrusionLayerSpecification
    | LineLayerSpecification
    | ModelLayerSpecification
    | CircleLayerSpecification
    | SymbolLayerSpecification;

/**
 * Basic Mapbox layer which adds functionality which most Mapbox layers will need.
 * This class is specifically for local layers and will handle the geojson source.
 */
export class MapboxLocalLayer<TSource extends Feature<Geometry>[], TLayer extends SupportedMapboxLayers> implements IMapboxLayer {
    private readonly map: Map;

    public readonly layerId: string;

    private readonly sourceId: string;

    constructor(map: Map, layerId: string, sourceId: string) {
        this.map = map;
        this.layerId = layerId;
        this.sourceId = sourceId;
    }

    public addLayer(_mapboxLayer: TLayer, aboveLayerId?: string) {
        const mapboxLayer = { ..._mapboxLayer, id: this.layerId, source: this.sourceId };
        // We don't need source-layer for local layers
        if (mapboxLayer["source-layer"] != null) {
            delete mapboxLayer["source-layer"];
        }
        if (!this.map.getLayer(mapboxLayer.id)) {
            // Ensure the layer has the source
            if (!this.map.getSource(mapboxLayer.source)) {
                this.addSource();
            }

            this.map.addLayer(mapboxLayer, aboveLayerId);
        }
    }

    public removeLayer() {
        this.map.removeLayer(this.layerId);
    }

    public addSource(): void {
        if (!this.map.getSource(this.sourceId)) {
            this.map.addSource(this.sourceId, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
        }
    }

    public updateSource(source: TSource) {
        let layerSource = this.map.getSource(this.sourceId);

        if (layerSource == null) {
            this.addSource();
            layerSource = this.map.getSource(this.sourceId);
        }

        if (layerSource != null && layerSource.type === "geojson") {
            layerSource.setData({ type: "FeatureCollection", features: source });
        }
    }

    public removeSource(): void {
        if (this.map.getSource(this.sourceId)) {
            this.map.removeSource(this.sourceId);
        }
    }

    public hideLayer(): void {
        this.map.setLayoutProperty(this.layerId, "visibility", "none");
    }

    public showLayer(): void {
        this.map.setLayoutProperty(this.layerId, "visibility", "visible");
    }

    public updateMapOrder(aboveLayerId: string): void {
        this.map.moveLayer(this.layerId, aboveLayerId);
    }

    public updateMapLevel(level: number) {
        const levelFilter = createMapboxLevelFilter(level);
        this.updateLayerFilter(levelFilter);
    }

    public addDateFilter() {
        const dateFilter = createDateFilterExpressionForLocalLayer();
        this.updateLayerFilter(dateFilter);
    }

    public removeDateFilter() {
        this.removeLayerFilter(FilterType.Date);
    }

    public updateLayerFilter(newFilter: LayerSpecification["filter"]) {
        const currentFilter = this.map.getFilter(this.layerId);
        const updatedFilter = updateLayerFilters(currentFilter, newFilter);
        this.map.setFilter(this.layerId, updatedFilter);
    }

    public removeLayerFilter(filterToRemove: FilterType) {
        const currentFilter = this.map.getFilter(this.layerId);
        const updatedFilter = removeLayerFilterByType(currentFilter, filterToRemove);
        this.map.setFilter(this.layerId, updatedFilter);
    }
}
